import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Shield, AlertTriangle, Lock, Laptop, Globe, Calendar, User } from 'lucide-react';
import apiClient from '../../services/api/client';

const VerifyLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const action = searchParams.get('action');

  const [status, setStatus] = useState('loading'); // loading, showing, confirming, confirmed, denied, blocked, error, no-token
  const [loginDetails, setLoginDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token || !action) {
      setStatus('no-token');
      return;
    }

    // Get login details from API
    const fetchLoginStatus = async () => {
      try {
        const response = await apiClient.get(`/security/login-status`, { params: { token } });
        if (response.data.valid) {
          setLoginDetails(response.data.login_details);
          setStatus('showing');
        } else {
          setErrorMessage(response.data.reason || 'Invalid or expired link');
          setStatus('error');
        }
      } catch (err) {
        // Network errors are handled by apiClient with retry + toast notification
        setErrorMessage('Une erreur est survenue');
        setStatus('error');
      }
    };

    fetchLoginStatus();
  }, [token, action]);

  const handleAction = async (selectedAction) => {
    setStatus('confirming');
    try {
      const response = await apiClient.post(`/security/verify-login`, {
        token: token,
        action: selectedAction
      });

      if (selectedAction === 'confirm') {
        setStatus('confirmed');
      } else {
        setStatus('blocked');
      }
    } catch (err) {
      // Network errors are handled by apiClient with retry + toast notification
      if (err.response?.data?.detail) {
        setErrorMessage(err.response.data.detail);
      } else {
        setErrorMessage('Une erreur est survenue');
      }
      setStatus('error');
    }
  };

  // Auto-process action from URL if present
  useEffect(() => {
    if (status === 'showing' && action && ['confirm', 'deny'].includes(action)) {
      // Don't auto-confirm for security - user must click
      // But for deny, we can show the confirmation screen
    }
  }, [status, action]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,136,0.3) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,136,0.2) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div
          className="bg-[#0d0d0d]/90 backdrop-blur-xl border border-gray-800 rounded-2xl p-8"
          style={{ boxShadow: '0 0 50px rgba(0, 255, 136, 0.1)' }}
        >
          {/* Loading */}
          {status === 'loading' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-brand-green animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Chargement...</h2>
              <p className="text-gray-400">Vérification de votre lien de sécurité...</p>
            </div>
          )}

          {/* Showing login details */}
          {status === 'showing' && loginDetails && (
            <>
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Shield className="w-10 h-10 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Vérification de connexion</h2>
                <p className="text-gray-400">
                  Une nouvelle connexion a été détectée sur votre compte SYLink Pentest
                </p>
              </div>

              {/* Login details */}
              <div className="bg-[#111111] border border-gray-800 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  Détails de la connexion
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Date et heure</p>
                      <p className="text-white">{loginDetails?.date || 'Non disponible'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Adresse IP</p>
                      <p className="text-white font-mono">{loginDetails?.ip || 'Non disponible'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Laptop className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Navigateur</p>
                      <p className="text-white">{loginDetails?.browser || 'Non disponible'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Système / Appareil</p>
                      <p className="text-white">{loginDetails?.os || 'Inconnu'} - {loginDetails?.device || 'Inconnu'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <p className="text-center text-gray-400 text-sm mb-4">
                  Cette connexion vous semble-t-elle légitime ?
                </p>
                <button
                  onClick={() => handleAction('confirm')}
                  className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Oui, c'est moi
                </button>
                <button
                  onClick={() => handleAction('deny')}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  Non, ce n'est pas moi
                </button>
              </div>

              <p className="text-center text-gray-500 text-xs mt-4">
                Si ce n'était pas vous, votre compte sera bloqué et notre équipe de support sera notifiée.
              </p>
            </>
          )}

          {/* Confirming */}
          {status === 'confirming' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-brand-green animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Traitement en cours...</h2>
              <p className="text-gray-400">Veuillez patienter</p>
            </div>
          )}

          {/* Confirmed */}
          {status === 'confirmed' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center animate-pulse">
                <CheckCircle className="w-10 h-10 text-brand-green" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Connexion confirmée !</h2>
              <p className="text-gray-400 mb-6">
                Merci d'avoir confirmé votre connexion. Votre compte est sécurisé.
              </p>
              <div className="bg-brand-green/10 border border-brand-green/20 rounded-xl p-4 mb-6">
                <Shield className="w-8 h-8 text-brand-green mx-auto mb-2" />
                <p className="text-brand-green text-sm">
                  Votre compte est protégé. Pensez à activer l'authentification à deux facteurs (MFA) pour plus de sécurité.
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors"
              >
                Retour à l'application
              </Link>
            </div>
          )}

          {/* Blocked */}
          {status === 'blocked' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <Lock className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Compte sécurisé</h2>
              <p className="text-gray-400 mb-6">
                Votre compte a été bloqué par mesure de sécurité. Notre équipe de support a été notifiée et vous contactera prochainement.
              </p>
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
                <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-red-400 text-sm">
                  Si vous avez besoin d'aide urgente, contactez notre support à support@sylink.fr
                </p>
              </div>
              <div className="space-y-3">
                <a
                  href="mailto:support@sylink.fr?subject=Compte%20bloque%20-%20Connexion%20suspecte"
                  className="inline-flex items-center justify-center gap-2 w-full bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  Contacter le support
                </a>
                <Link
                  to="/"
                  className="block text-brand-green hover:text-brand-green/80 text-sm transition-colors"
                >
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Erreur</h2>
              <p className="text-gray-400 mb-6">{errorMessage}</p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors"
              >
                Retour à la connexion
              </Link>
            </div>
          )}

          {/* No token */}
          {status === 'no-token' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Lien invalide</h2>
              <p className="text-gray-400 mb-6">
                Ce lien de vérification est invalide ou a expiré. Veuillez demander un nouveau lien.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors"
              >
                Retour à la connexion
              </Link>
            </div>
          )}
        </div>

        {/* Logo */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">
            SYLink Pentest - Sécurité de connexion
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyLogin;
